import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { apiService, type IAPIResponse } from '@/services/api'
import { useAuthStore } from './useAuthStore'

// Types
export interface EISProductDto {
  id: number
  productCode: string
  productName: string
  description: string
  price: number
  quantity: number
  unitOfMeasure: string
  taxRateId: string
  sageItemCode: string | null
  isMappedToSage: boolean
  lastSyncDate: string | null
  mappingReason: string | null
  isMapped: boolean
}

export interface LowStockReportDto {
  productCode: string
  productName: string
  currentQuantity: number
  minimumLevel: number
  deficit: number
  price: number
  sageItemCode: string
}

export interface InventoryValueDto {
  totalProducts: number
  totalQuantity: number
  totalValue: number
  lowStockCount: number
  outOfStockCount: number
}

export interface EISProductsResponse {
  products: EISProductDto[]
  totalCount: number
  page: number
  pageSize: number
  totalPages: number
  mappedCount: number
  unmappedCount: number
}

export interface POSProductDto {
  sageItemCode: string
  sageStockLink: number
  eisProductCode: string
  productName: string
  description: string
  price: number
  qtyAvailable: number
  barcode: string
  taxRate: number
  taxRateId: string
  unitOfMeasure: string
  canSell: boolean
  isMapped: boolean
}

export interface UnmappedProductsResponse {
  products: Array<{
    productCode: string
    productName: string
    description: string
    price: number
    unitOfMeasure: string
    source: string
  }>
  totalCount: number
  page: number
  pageSize: number
  totalPages: number
  unmappedEISCount: number
  unmappedSageCount: number
}

export interface ProductMappingDto {
  eisProductCode: string
  sageItemCode: string
}

export interface SyncResult {
  success: boolean
  errorMessage?: string
  successfulSyncs?: number
  failedSyncs?: number
  errors?: string[]
}

export interface MappingResult {
  success: boolean
  errorMessage?: string
}

export interface InventorySyncResponse {
  sync: SyncResult
  mapping: MappingResult
}

export interface EISSyncLogDto {
  id: number
  terminalId: string
  syncDate: string
  duration: number
  totalRecordsDownloaded: number
  newRecords: number
  updatedRecords: number
  errorCount: number
  errorDetails: string | null
  syncType: string
  syncedBy: string
}

// NEW: Types for SAGE sync functionality
export interface UnmappedTransactionInfo {
  transactionId: number
  transactionNumber: string
  transactionDate: string
  totalAmount: number
  unmappedProductCodes: string | null
}

export interface PendingSageSyncResponse {
  count: number
  transactions: Array<{
    transactionId: number
    transactionNumber: string
    transactionDate: string
    totalAmount: number
    unmappedProductCodes: string | null
    hasUnmappedProducts: boolean
  }>
}

export interface UnmappedTransactionsResponse {
  eisProductCode: string
  count: number
  transactions: UnmappedTransactionInfo[]
}

export interface SageSyncResult {
  message: string
  syncResult: {
    success: boolean
    eisProductCode: string
    sageItemCode: string
    totalTransactionsFound: number
    successfulSyncs: number
    failedSyncs: number
    syncedTransactionIds: number[]
    failedTransactionIds: number[]
    errors: string[]
    startTime: string
    endTime: string
    duration: string
  }
}

export interface MappingResponse {
  message: string
  unmappedTransactionsFound: number
  syncPerformed: boolean
  syncResult: SyncResult | null
}

export const useEISInventoryStore = defineStore('eisInventory', () => {
  // State
  const eisProducts = ref<EISProductDto[]>([])
  const posProducts = ref<POSProductDto[]>([])
  const unmappedProducts = ref<UnmappedProductsResponse | null>(null)
  const syncHistory = ref<EISSyncLogDto[]>([])
  const loading = ref(false)
  const syncLoading = ref(false)
  const error = ref<string | null>(null)

  const lowStockProducts = ref<LowStockReportDto[]>([])
  const inventoryValue = ref<InventoryValueDto | null>(null)
  const authStore = useAuthStore()
  const pendingSageSyncTransactions = ref<PendingSageSyncResponse | null>(null)
  const unmappedTransactionsForProduct = ref<UnmappedTransactionsResponse | null>(null)
  const sageSyncLoading = ref(false)

  // Computed
  const mappedProducts = computed(() => eisProducts.value.filter((p) => p.isMappedToSage))

  const unmappedEISProducts = computed(() => eisProducts.value.filter((p) => !p.isMappedToSage))

  const totalProducts = computed(() => eisProducts.value.length)

  const mappedCount = computed(() => mappedProducts.value.length)

  const unmappedCount = computed(() => unmappedEISProducts.value.length)

  const mappingPercentage = computed(() => {
    if (totalProducts.value === 0) return 0
    return Math.round((mappedCount.value / totalProducts.value) * 100)
  })

  // NEW: Computed for SAGE sync status
  const hasPendingSageSync = computed(() => {
    return (pendingSageSyncTransactions.value?.count ?? 0) > 0
  })

  const pendingSageSyncCount = computed(() => {
    return pendingSageSyncTransactions.value?.count ?? 0
  })

  // Actions

  /**
   * Load all EIS products with pagination
   * @param mappedOnly - Filter by mapping status (true: only mapped, false: only unmapped, undefined: all)
   * @param page - Page number (default: 1)
   * @param pageSize - Number of items per page (default: 50)
   */
  const loadEISProducts = async (mappedOnly?: boolean, page: number = 1, pageSize: number = 50) => {
    loading.value = true
    error.value = null

    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      })

      if (mappedOnly !== undefined) {
        params.append('mappedOnly', mappedOnly.toString())
      }

      const response = await apiService.get<IAPIResponse<EISProductsResponse>>(
        `/eis/eisproducts?${params.toString()}`,
      )

      if (response.isSuccessful && response.payload) {
        eisProducts.value = response.payload.products
        return response.payload // Return the full response with pagination data
      } else {
        error.value = response.remark || 'Failed to load EIS products'
        return null
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load EIS products'
      console.error('Error loading EIS products:', err)
      return null
    } finally {
      loading.value = false
    }
  }

  const getLowStockProducts = async () => {
    loading.value = true
    error.value = null
    try {
      const response = await apiService.get<IAPIResponse<LowStockReportDto[]>>(
        '/eis/eisproducts/reports/low-stock',
      )

      if (response.isSuccessful && response.payload) {
        lowStockProducts.value = response.payload
      } else {
        error.value = response.remark || 'Failed to load low stock products'
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load low stock products'
    } finally {
      loading.value = false
    }
  }

  const getInventoryValue = async () => {
    loading.value = true
    error.value = null
    try {
      const response = await apiService.get<IAPIResponse<InventoryValueDto>>(
        '/eis/eisproducts/reports/inventory-value',
      )

      if (response.isSuccessful && response.payload) {
        inventoryValue.value = response.payload
      } else {
        error.value = response.remark || 'Failed to load inventory value'
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load inventory value'
    } finally {
      loading.value = false
    }
  }

  const loadPOSProducts = async (search?: string) => {
    loading.value = true
    error.value = null

    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : ''

      const response = await apiService.get<IAPIResponse<POSProductDto[]>>(
        `/eis/eisproducts/pos-products${params}`,
      )

      if (response.isSuccessful && response.payload) {
        posProducts.value = response.payload
      } else {
        error.value = response.remark || 'Failed to load POS products'
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load POS products'
      console.error('Error loading POS products:', err)
    } finally {
      loading.value = false
    }
  }

  /**
   * Load unmapped products from both EIS and Sage
   */
  const loadUnmappedProducts = async (page: number = 1, pageSize: number = 50, source?: string) => {
    loading.value = true
    error.value = null

    try {
      // Build query parameters
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      })

      // Add optional source filter
      if (source) {
        params.append('source', source)
      }

      if (authStore.isSageEnabled) {
        const response = await apiService.get<IAPIResponse<UnmappedProductsResponse>>(
          `/eis/eisproducts/unmapped?${params.toString()}`,
        )
        if (response.isSuccessful && response.payload) {
          unmappedProducts.value = response.payload
        } else {
          error.value = response.remark || 'Failed to load unmapped products'
        }
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load unmapped products'
      console.error('Error loading unmapped products:', err)
    } finally {
      loading.value = false
    }
  }

  /**
   * Map an EIS product to a Sage product
   * NEW: Now includes auto-sync of unmapped transactions
   */
  const mapProduct = async (mapping: ProductMappingDto): Promise<MappingResponse | null> => {
    loading.value = true
    error.value = null

    try {
      const response = await apiService.post<IAPIResponse<MappingResponse>>(
        '/eis/eisproducts/map',
        mapping,
      )

      if (response.isSuccessful) {
        // Refresh the product lists and pending sync status after successful mapping
        await Promise.all([
          loadEISProducts(),
          loadUnmappedProducts(),
          loadPendingSageSyncTransactions(),
        ])
        return response.payload!
      } else {
        error.value = response.remark || 'Failed to map product'
        return null
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to map product'
      console.error('Error mapping product:', err)
      return null
    } finally {
      loading.value = false
    }
  }

  /**
   * Unmap an EIS product from Sage
   */
  const unmapProduct = async (eisProductCode: string): Promise<boolean> => {
    loading.value = true
    error.value = null

    try {
      const response = await apiService.delete<IAPIResponse<any>>(
        `/eis/eisproducts/map/${eisProductCode}`,
      )

      if (response.isSuccessful) {
        // Refresh the product lists after successful unmapping
        await loadEISProducts()
        if (authStore.isSageEnabled) {
          await loadUnmappedProducts()
        }
        return true
      } else {
        error.value = response.remark || 'Failed to unmap product'
        return false
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to unmap product'
      console.error('Error unmapping product:', err)
      return false
    } finally {
      loading.value = false
    }
  }

  /**
   * Sync products from EIS for a specific terminal
   */
  const syncProductsFromEIS = async (terminalId: string): Promise<InventorySyncResponse | null> => {
    syncLoading.value = true
    error.value = null

    try {
      const response = await apiService.post<IAPIResponse<InventorySyncResponse>>(
        `/eis/eisproducts/sync/${terminalId}`,
        {},
      )

      if (response.isSuccessful && response.payload) {
        // Refresh products after sync
        await loadEISProducts()
        return response.payload
      } else {
        error.value = response.remark || 'Failed to sync products from EIS'
        return null
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to sync products from EIS'
      console.error('Error syncing products:', err)
      return null
    } finally {
      syncLoading.value = false
    }
  }

  /**
   * Load sync history for a terminal
   */
  const loadSyncHistory = async (terminalId: string, pageSize: number = 20) => {
    loading.value = true
    error.value = null

    try {
      const response = await apiService.get<IAPIResponse<EISSyncLogDto[]>>(
        `/eis/eisproducts/sync-history/${terminalId}?pageSize=${pageSize}`,
      )

      if (response.isSuccessful && response.payload) {
        syncHistory.value = response.payload
      } else {
        error.value = response.remark || 'Failed to load sync history'
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load sync history'
      console.error('Error loading sync history:', err)
    } finally {
      loading.value = false
    }
  }

  // NEW: SAGE Sync Actions

  /**
   * Load all transactions that need SAGE synchronization
   */
  const loadPendingSageSyncTransactions = async () => {
    loading.value = true
    error.value = null

    try {
      if (authStore.isSageEnabled) {
        const response = await apiService.get<IAPIResponse<PendingSageSyncResponse>>(
          '/eis/eisproducts/pending-sage-sync',
        )

        if (response.isSuccessful && response.payload) {
          pendingSageSyncTransactions.value = response.payload
        } else {
          error.value = response.remark || 'Failed to load pending SAGE sync transactions'
        }
      }
    } catch (err) {
      error.value =
        err instanceof Error ? err.message : 'Failed to load pending SAGE sync transactions'
      console.error('Error loading pending SAGE sync transactions:', err)
    } finally {
      loading.value = false
    }
  }

  /**
   * Get unmapped transactions for a specific product
   * @param eisProductCode - The EIS product code to check
   */
  const loadUnmappedTransactionsForProduct = async (eisProductCode: string) => {
    loading.value = true
    error.value = null

    try {
      const response = await apiService.get<IAPIResponse<UnmappedTransactionsResponse>>(
        `/eis/eisproducts/unmapped-transactions/${eisProductCode}`,
      )

      if (response.isSuccessful && response.payload) {
        unmappedTransactionsForProduct.value = response.payload
        return response.payload
      } else {
        error.value = response.remark || 'Failed to load unmapped transactions for product'
        return null
      }
    } catch (err) {
      error.value =
        err instanceof Error ? err.message : 'Failed to load unmapped transactions for product'
      console.error('Error loading unmapped transactions for product:', err)
      return null
    } finally {
      loading.value = false
    }
  }

  /**
   * Manually trigger SAGE sync for unmapped transactions of a product
   * @param eisProductCode - The EIS product code to sync
   */
  const syncUnmappedTransactions = async (
    eisProductCode: string,
  ): Promise<SageSyncResult | null> => {
    sageSyncLoading.value = true
    error.value = null

    try {
      const response = await apiService.post<IAPIResponse<SageSyncResult>>(
        '/eis/eisproducts/sync-unmapped-transactions',
        { eisProductCode },
      )

      if (response.isSuccessful && response.payload) {
        // Refresh pending sync transactions after successful sync
        await loadPendingSageSyncTransactions()
        return response.payload
      } else {
        error.value = response.remark || 'Failed to sync unmapped transactions'
        return null
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to sync unmapped transactions'
      console.error('Error syncing unmapped transactions:', err)
      return null
    } finally {
      sageSyncLoading.value = false
    }
  }

  /**
   * Get statistics for EIS inventory
   */
  const getInventoryStats = () => {
    return {
      total: totalProducts.value,
      mapped: mappedCount.value,
      unmapped: unmappedCount.value,
      mappingPercentage: mappingPercentage.value,
      totalValue: eisProducts.value.reduce((sum, p) => sum + p.price * p.quantity, 0),
      pendingSageSync: pendingSageSyncCount.value,
    }
  }

  /**
   * Search products locally
   */
  const searchProducts = (searchTerm: string) => {
    if (!searchTerm) return eisProducts.value

    const term = searchTerm.toLowerCase()
    return eisProducts.value.filter(
      (p) =>
        p.productName.toLowerCase().includes(term) ||
        p.productCode.toLowerCase().includes(term) ||
        p.description?.toLowerCase().includes(term),
    )
  }

  /**
   * Clear error state
   */
  const clearError = () => {
    error.value = null
  }

  /**
   * Reset store state
   */
  const resetStore = () => {
    eisProducts.value = []
    posProducts.value = []
    unmappedProducts.value = null
    syncHistory.value = []
    loading.value = false
    syncLoading.value = false
    sageSyncLoading.value = false
    error.value = null
    pendingSageSyncTransactions.value = null
    unmappedTransactionsForProduct.value = null
  }

  return {
    // State
    eisProducts,
    posProducts,
    unmappedProducts,
    syncHistory,
    loading,
    syncLoading,
    error,
    lowStockProducts,
    inventoryValue,
    pendingSageSyncTransactions,
    unmappedTransactionsForProduct,
    sageSyncLoading,

    // Computed
    mappedProducts,
    unmappedEISProducts,
    totalProducts,
    mappedCount,
    unmappedCount,
    mappingPercentage,
    hasPendingSageSync,
    pendingSageSyncCount,

    // Actions
    loadEISProducts,
    loadPOSProducts,
    loadUnmappedProducts,
    mapProduct,
    unmapProduct,
    syncProductsFromEIS,
    loadSyncHistory,
    getInventoryStats,
    searchProducts,
    clearError,
    resetStore,
    getLowStockProducts,
    getInventoryValue,
    // NEW: SAGE Sync Actions
    loadPendingSageSyncTransactions,
    loadUnmappedTransactionsForProduct,
    syncUnmappedTransactions,
  }
})
